import { useCallback, useState, type CSSProperties } from 'react';
// @ts-expect-error - @types/react-grid-layout@1.3.4 missing internal build types
import { Responsive, WidthProvider, utils as rglUtils, type Layout } from 'react-grid-layout';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import { pixelsToLayoutRows, sharedFlexRGLProps } from 'lib/FlexibleLayoutUtils';
import ViewBlockItem from './ViewBlockItem';
import { MeasuredContent } from './MeasuredContent';
import BlockItemModel from '../../editor/model/BlockItemModel';
import { type BlockComponent, ComponentType } from 'types';
import { selectors as storySelectors } from 'store/selectors/StorySelectors';
import type { StorytellerState } from 'store/StorytellerReduxState';

// @ts-expect-error TS(7009) FIXME: 'new' expression, whose target lacks a construct s... Remove this comment to see the full error message
const ResponsiveReactGridLayout = new WidthProvider(Responsive);

// Text sizes to its content, so its stored grid height is only the value last
// measured in the editor and is wrong at view/print widths. Those types are
// measured here; everything else keeps its stored height.
const isContentHeightType = (type: ComponentType): boolean =>
  type === ComponentType.HTML || type === ComponentType.HTML_TABLE_OF_CONTENTS;

/**
 * Renders a flexible-layout block for two targets:
 * - On screen: react-grid-layout positions the blocks. Text blocks are measured
 *   (MeasuredContent) so RGL sizes them to their rendered height — the `textRowHeights`
 *   / `layout` path below.
 * - In print: RGL's absolute transforms can't paginate, so story-view.scss reflows
 *   the same tree into a CSS grid at print time. That grid reads each block's position
 *   from CSS variables we publish here — the `compactedLayout` / `printGridVars` path.
 *   These are inert on screen.
 */
export function ViewBlockSection({ blockId, components }: { components: BlockComponent[]; blockId: string }) {
  const backgroundColor = useSelector((state: StorytellerState) =>
    storySelectors.getBlockBackgroundColor(state, blockId),
  );

  const isPresentable = useSelector((state: StorytellerState) =>
    storySelectors.isBlockPresentable(state, blockId),
  );

  // Measured text heights in grid rows, keyed by component id.
  const [textRowHeights, setTextRowHeights] = useState<{ [id: string]: number }>({});

  const onTextHeightChange = useCallback((id: string, pixels: number) => {
    const rows = pixelsToLayoutRows(pixels);
    setTextRowHeights((prev) => (prev[id] === rows ? prev : { ...prev, [id]: rows }));
  }, []);

  const generateBlockItemModels = (blockItemData: BlockComponent[]): BlockItemModel[] => {
    return blockItemData.map((blockItem, index: number) => {
      return new BlockItemModel(
        blockItem.layout,
        blockItem.type,
        blockItem.value,
        index,
        blockId,
        blockItem.componentUuid,
      );
    });
  };

  // Applies default layouts and ids to components. Sorted by grid position (y, then x) so
  // DOM/focus order matches visual order — RGL only repositions items visually via CSS
  // transforms (matching children to `layouts` entries by `layout.i`, not array position),
  // it never reorders the DOM itself. Without this, dragging a component above one created
  // earlier leaves it later in the DOM, breaking screen reader and keyboard tab order.
  const blockItems = generateBlockItemModels(components).sort(
    (a, b) => a.layout.y - b.layout.y || a.layout.x - b.layout.x,
  );

  // View: drive RGL from an explicit layout array (as the editor does) rather than
  // per-child data-grid, which RGL ignores after mount. Text heights come from the
  // measured values above. Items are intentionally not static: static pins an item
  // at its authored y, so a text block measured taller than its stored height would
  // overlap the block below. Non-static lets RGL's vertical compaction re-pack as
  // heights change (drag/resize stay disabled at the grid level).
  const layout = blockItems.map((blockItem) => {
    const measuredHeight = isContentHeightType(blockItem.type) ? textRowHeights[blockItem.id] : undefined;
    return {
      ...blockItem.layout,
      ...(measuredHeight != null ? { h: measuredHeight } : {}),
    };
  });

  // Print-only: the print stylesheet reflows this grid into document flow so a
  // visualization isn't sliced across a page break, and it needs each block's position.
  // Those positions come from RGL's vertical compaction (blocks shift once measured
  // text heights are applied), so we run the same compaction here and expose the
  // result as CSS variables below.
  //
  // Round to whole rows/columns first: CSS grid line numbers must be integers, and a
  // stored fractional value (e.g. y = 9.9) yields an invalid grid-row-start that the
  // browser ignores, collapsing the block onto row 1. `compact` keeps input order,
  // so entry `i` maps to `blockItems[i]`.
  const roundedLayout: Layout[] = layout.map((item) => ({
    ...item,
    x: Math.round(item.x),
    y: Math.round(item.y),
    w: Math.max(1, Math.round(item.w)),
    h: Math.max(1, Math.round(item.h)),
  }));
  const compactedLayout: Layout[] = rglUtils.compact(roundedLayout, 'vertical', sharedFlexRGLProps.cols.lg);

  const renderBlockSectionItem = () => {
    return blockItems.map((blockItem: BlockItemModel, index: number) => {
      const item = (
        <ViewBlockItem
          blockId={blockId}
          componentIndex={blockItem.index}
          component={{
            id: blockItem.id,
            type: blockItem.type,
            value: blockItem.value,
            layout: blockItem.layout,
            componentUuid: blockItem.componentUuid,
          }}
        />
      );

      // Print-only: expose the block's compacted grid coordinates as CSS variables.
      // RGL clones this element and merges its own transform/width/height onto it, so
      // the variables land on the same .react-grid-item the print stylesheet reflows.
      // Inert on screen (RGL positions with transforms).
      const { x, y, w, h } = compactedLayout[index];
      const printGridVars = {
        '--flex-col-start': x + 1,
        '--flex-col-span': w,
        '--flex-row-start': y + 1,
        '--flex-row-span': h,
      } as CSSProperties;

      return (
        <div key={blockItem.layout.i} style={printGridVars}>
          {isContentHeightType(blockItem.type) ? (
            <MeasuredContent onHeightChange={(pixels) => onTextHeightChange(blockItem.id, pixels)}>
              {item}
            </MeasuredContent>
          ) : (
            item
          )}
        </div>
      );
    });
  };

  const isGlobalFilter = components[0] != null && components[0].type === ComponentType.GLOBAL_FILTER;
  const blockClasses = classNames('block', {
    sticky: isGlobalFilter,
  });

  return (
    <div
      className={blockClasses}
      style={{ backgroundColor }}
      data-block-id={blockId}
      data-presentable={isPresentable}
    >
      <div className="block-content">
        {isGlobalFilter ? (
          renderBlockSectionItem()
        ) : (
          <ResponsiveReactGridLayout
            layouts={{ lg: layout, md: layout, sm: layout, xs: layout, xxs: layout }}
            className={sharedFlexRGLProps.className}
            cols={sharedFlexRGLProps.cols}
            rowHeight={sharedFlexRGLProps.rowHeight}
            isDraggable={false}
            isResizable={false}
            isDroppable={false}
            containerPadding={sharedFlexRGLProps.containerPadding}
            margin={sharedFlexRGLProps.margin}
            // setting this to false forces D3 to not rely on `transform` for positioning.
            // if we don't set it to false, the location of `d3.mouse` will somehow return
            // the wrong cursor location.
            // see https://github.com/elastic/kibana/issues/16870 for a similar issue.
            // useCSSTransforms={false}
          >
            {renderBlockSectionItem()}
          </ResponsiveReactGridLayout>
        )}
      </div>
    </div>
  );
}
